import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IconClose } from '../../Icon';
import styles from './AlertBanner.module.scss';
import { RootState } from '../../../store';
import { clearGlobalAlert } from '../../../store/app/slice';

export type AlertType = 'error' | 'warning' | 'info' | 'success';

export interface AlertBannerProps {
  /**
   * Optional type override - if not provided, uses the global alert's type
   */
  type?: AlertType;

  /**
   * Optional message override - if not provided, uses the global alert's message
   */
  message?: string;

  /**
   * Optional additional content to display below the main message
   */
  children?: React.ReactNode;

  /**
   * Whether the alert can be dismissed by the user
   */
  dismissible?: boolean;

  /**
   * Optional custom action component (e.g., a button)
   */
  action?: React.ReactNode;

  /**
   * Additional CSS class to apply to the component
   */
  className?: string;
}

/**
 * AlertBanner component for displaying different types of notifications.
 * Uses the global alert state from the store by default, but can be overridden with props.
 */
const AlertBanner: React.FC<AlertBannerProps> = ({
  type: typeProp,
  message: messageProp,
  children,
  dismissible = true,
  action,
  className = '',
}) => {
  const dispatch = useDispatch();
  const globalAlert = useSelector((state: RootState) => state.app.alert);

  const message = messageProp || globalAlert?.message;
  if (!message) {
    return null;
  }

  const handleDismiss = () => {
    dispatch(clearGlobalAlert());
  };

  // Use props if provided, otherwise fall back to global alert, then to defaults
  const type = typeProp || (globalAlert?.type.toLowerCase() as AlertType) || 'warning';

  const alertClasses = [styles.alertBanner, styles[type], className].filter(Boolean).join(' ');

  return (
    <div className={alertClasses} role="alert">
      <div className={styles.content}>
        <div className={styles.message}>{message}</div>
        {children && <div className={styles.details}>{children}</div>}
      </div>

      <div className={styles.actions}>
        {action && <div className={styles.actionContainer}>{action}</div>}
        {dismissible && (
          <button
            className={styles.dismissButton}
            onClick={handleDismiss}
            aria-label="Dismiss alert"
          >
            <IconClose size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default AlertBanner;
